// src/admin/sections/AffiliateSection.jsx
//
// Ticket 48. One card and one list.
//
// THE CARD. The three affiliate tags, read-only and "set in code". Every value is
// read from AFFILIATE in src/lib/retailers.js, the file the reader app tags links
// from, so the card cannot show a tag the app does not use. scripts/check-book-
// link-parity.mjs fails the build if a tag value is typed into this file.
//
// THE TAG FOLLOWS THE LINK'S STORE, NOT THE READER'S COUNTRY. amazon.ca gets the
// Canada tag and amazon.com the US one (withAffiliate, retailers.js: "This is NOT
// reader geo-routing"). So there is no routing check here and no line about
// readers in other countries: both would describe a rule that does not exist.
//
// THE LIST. Live books a reader cannot buy. A book's Buy buttons come from two
// stores (the purchase_links table and the books.purchase_links column) and then
// pass through isBookLink, which drops any link that is not a page for the book
// itself. So the count and the list come from the database, from a function that
// applies the same rule (admin_buy_link_counts, admin_books_nowhere_to_buy:
// 20261030100000_ticket48_nowhere_to_buy.sql), not from a count of link rows.
// Overview's row reads the same function, so the three numbers agree.
//
// "OPEN BOOK" opens that book on the Books screen (/admin/r-books?book=id), on the
// tab it is on, with its buy links open (ticket 118). An admin adds or fixes a link
// there; an author can still add one in the author app. Nothing here generates one.
//
// NOT WIRED: tap counts and link health. Nothing logs an outbound tap with a
// retailer or link on it, and a dead retailer page does not throw, so Sentry
// never sees it. Both are marked, not shown as if they were real.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { AFFILIATE } from '../../lib/retailers';
import { loadBuyLinkCounts } from '../lib/buyLinkCounts';
import InfoTip from '../components/InfoTip';

const PAGE = 20;

// A label and the stores a tag is added to, per key of AFFILIATE. The values come
// from retailers.js; a key added there without a row here still shows, by its key.
const TAG_INFO = {
  amazonUS: { label: 'Amazon US', where: 'links to amazon.com' },
  amazonCA: { label: 'Amazon CA', where: 'links to amazon.ca' },
  bookshop: { label: 'Bookshop',  where: 'bookshop.org links that carry an ISBN. A link without one is left as it came.' },
};

const KINDS = [
  { id: 'all',      label: 'All' },
  { id: 'none',     label: 'No link saved' },
  { id: 'rejected', label: 'Links readers can’t use' },
];

// What an empty list says, per filter. Each says only what that filter shows: an empty
// "No link saved" does not mean every book can be bought.
const EMPTY = {
  all:      'Every live book has a Buy link readers can use',
  none:     'No live book is missing a saved link',
  rejected: 'Every live book with a saved link has one readers can use',
};

const fmt = n => (n === null || n === undefined ? '—' : n.toLocaleString());

const TONE = {
  warn: { bg: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: 'var(--feedback-warning-border)' },
  bad:  { bg: 'var(--feedback-error-bg)', color: 'var(--feedback-error)', border: 'var(--feedback-error)' },
};

function Pill({ children, tone }) {
  const t = TONE[tone] || { bg: '#f5f0e8', color: 'var(--neutral-600)', border: 'var(--brand-sand)' };
  return (
    <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-md)', background: t.bg, color: t.color, border: `1px solid ${t.border}`, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function Btn({ children, onClick, isMobile, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        padding: isMobile ? '0 16px' : '7px 14px', minHeight: isMobile ? 44 : undefined,
        background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)',
        borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 700,
        cursor: 'pointer', fontFamily: 'var(--font-body)',
      }}
    >
      {children}
    </button>
  );
}

function Stat({ label, tip, value, sub }) {
  return (
    <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: '14px 16px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}<InfoTip text={tip} />
      </div>
      <div data-stat={label} style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--neutral-800)', lineHeight: 1.1, marginTop: 4 }}>{fmt(value)}</div>
      {sub && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function NotWired({ title, why }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, color: 'var(--neutral-800)', fontSize: 'var(--font-size-base)' }}>{title}</span>
        <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, letterSpacing: 0.5, padding: '2px 6px', borderRadius: 'var(--radius-md)', border: '1px solid var(--feedback-warning-border)', color: 'var(--feedback-warning-ink)', background: 'var(--brand-white)' }}>NOT WIRED</span>
      </div>
      <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', lineHeight: 1.5 }}>{why}</p>
    </div>
  );
}

function BookRow({ book, isMobile, onNavigate }) {
  const held = book.links_held || 0;
  return (
    <div data-nowhere-row={book.book_id} style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? 12 : 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10, justifyContent: 'space-between' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: 'var(--neutral-800)', fontSize: 'var(--font-size-md)' }}>{book.title || `Book ${book.book_id}`}</div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', marginTop: 2 }}>
            {book.author_name || 'No author name'} &middot; #{book.book_id}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {held === 0
              ? <Pill tone="warn">no link saved</Pill>
              : <Pill tone="bad">{held === 1 ? '1 link saved' : `${held} links saved`}, none a book page</Pill>}
            {book.author_id ? <Pill>has an author</Pill> : <Pill>no author account</Pill>}
          </div>
          {held > 0 && (
            <p style={{ margin: '8px 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', lineHeight: 1.5 }}>
              Readers see no Buy button. The reader app drops a link that is not a page for the book itself: an author page, a search, a shop front.
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start', flexShrink: 0 }}>
          <Btn isMobile={isMobile} onClick={() => onNavigate?.(`r-books?book=${book.book_id}`)} title="Opens this book in Books, with its buy links open.">Open book</Btn>
          {book.author_id && (
            <Btn isMobile={isMobile} onClick={() => onNavigate?.('a-authors')} title="Opens Authors. There is no link to one author yet, so find them by name.">Open Authors</Btn>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AffiliateSection({ onNavigate, isMobile }) {
  const [counts, setCounts] = useState(undefined);   // undefined: loading, null: could not load
  const [kind, setKind] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [more, setMore] = useState(false);
  const loadId = useRef(0);

  const refreshCounts = useCallback(async () => { setCounts(await loadBuyLinkCounts('Affiliate')); }, []);

  const loadRows = useCallback(async (from = 0) => {
    const id = ++loadId.current;
    setListError(null);
    if (from === 0) setLoading(true);
    const { data: page, error } = await supabase.rpc('admin_books_nowhere_to_buy', { p_kind: kind, p_limit: PAGE, p_offset: from });
    if (id !== loadId.current) return;
    if (error) {
      console.error('[Affiliate] list failed:', error.message);
      setListError(error.message);
      if (from === 0) setRows([]);
      setLoading(false);
      return;
    }
    setRows(prev => (from === 0 ? page : [...prev, ...page]));
    setMore(page.length === PAGE);
    setLoading(false);
  }, [kind]);

  useEffect(() => { refreshCounts(); }, [refreshCounts]);
  useEffect(() => { loadRows(0); }, [loadRows]);

  const c = counts || null;
  const share = c && c.live > 0 ? `${Math.round((c.nowhere / c.live) * 100)}% of ${fmt(c.live)} live books` : undefined;
  const kindCount = { all: c?.nowhere, none: c?.noLink, rejected: c?.rejected };
  const pad = isMobile ? 16 : '30px 36px';

  return (
    <div style={{ padding: pad, fontFamily: 'var(--font-body)' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, lineHeight: 1.1, margin: 0 }}>Affiliate Links</h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 4 }}>
          Which tag each store uses, and which live books have nowhere for a reader to buy them.
        </p>
      </div>

      {/* ── The card: the three tags, read from retailers.js ── */}
      <div data-affiliate-tags style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '14px 12px' : '20px 22px', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', margin: 0 }}>Affiliate tags</h2>
          <Pill>set in code</Pill>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', lineHeight: 1.5 }}>
          Read-only. Changing a tag means changing <code>src/lib/retailers.js</code>. The tag follows the store the link points at, not the reader&apos;s country: a link to amazon.ca gets the Canada tag and a link to amazon.com the US one.
        </p>
        {Object.entries(AFFILIATE).map(([key, tag]) => {
          const info = TAG_INFO[key] || { label: key, where: '' };
          return (
            <div key={key} data-affiliate-tag={key} style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : 14, alignItems: isMobile ? 'flex-start' : 'baseline', padding: '8px 0', borderTop: '1px solid var(--brand-cream)' }}>
              <span style={{ width: isMobile ? 'auto' : 120, flexShrink: 0, fontWeight: 700, color: 'var(--neutral-800)', fontSize: 'var(--font-size-base)' }}>{info.label}</span>
              <code data-tag-value style={{ width: isMobile ? 'auto' : 190, flexShrink: 0, fontSize: 'var(--font-size-sm)', color: 'var(--neutral-800)', background: '#faf8f4', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', padding: '2px 8px', wordBreak: 'break-all' }}>{tag}</code>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)' }}>{info.where ? `Added to ${info.where}` : ''}</span>
            </div>
          );
        })}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 24, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--brand-cream)' }}>
          <NotWired title="Tap counts" why="Nothing logs an outbound tap with a store or link on it yet, so there is no number to show." />
          <NotWired title="Link health" why="A dead retailer page does not throw, so Sentry never sees it. This needs the taps to be logged first." />
        </div>
      </div>

      {/* ── The list: live books a reader can't buy ── */}
      {counts === null && (
        <div role="alert" style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 'var(--radius-md)', background: TONE.warn.bg, color: TONE.warn.color, border: `1px solid ${TONE.warn.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
          Couldn&apos;t load the counts. A dash means unknown, not zero.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12, marginBottom: 18 }}>
        <Stat label="Live books" value={c?.live} tip="Books with status live." />
        <Stat
          label="Nowhere to buy"
          value={c?.nowhere}
          sub={share}
          tip="Live books that show readers no Buy button. A book counts as buyable when it has at least one link, in either place links are kept, that is a page for the book itself. A search page, an author page or a shop front does not count: the reader app drops those."
        />
        <Stat
          label="Links readers can't use"
          value={c?.rejected}
          sub={c ? `${fmt(c.noLink)} have no link saved at all` : undefined}
          tip="Live books that have a link saved but none the reader app will show, so the link is there and no Buy button is."
        />
      </div>

      <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '14px 12px' : '20px 22px' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>Live books with nowhere to buy them</h2>
        <p style={{ margin: '0 0 12px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', lineHeight: 1.5 }}>
          Nothing generates a link: they are typed in by an author or imported. To fix one, find the book, or ask its author to add a store link in the author app.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {KINDS.map(k => (
            <button key={k.id} type="button" data-kind={k.id} onClick={() => setKind(k.id)} style={{
              padding: isMobile ? '0 14px' : '5px 12px', minHeight: isMobile ? 44 : undefined, borderRadius: 'var(--radius-pill)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)',
              background: kind === k.id ? 'var(--neutral-800)' : 'var(--brand-white)', color: kind === k.id ? 'var(--brand-white)' : 'var(--neutral-600)', border: '1px solid var(--brand-sand)',
            }}>{k.label} ({fmt(kindCount[k.id])})</button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading&hellip;</div>
        ) : rows.length === 0 && listError ? (
          <div data-load-failed role="alert" style={{ padding: '12px 14px', borderRadius: 'var(--radius-md)', background: TONE.bad.bg, color: TONE.bad.color, border: `1px solid ${TONE.bad.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
            Couldn&apos;t load this list: {listError}
            <div style={{ marginTop: 8 }}><Btn isMobile={isMobile} onClick={() => { refreshCounts(); loadRows(0); }}>Try again</Btn></div>
          </div>
        ) : rows.length === 0 ? (
          <div data-nothing-here={kind} style={{ padding: '36px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
            &#10003; {EMPTY[kind]}
          </div>
        ) : (
          <>
            {rows.map(b => <BookRow key={b.book_id} book={b} isMobile={isMobile} onNavigate={onNavigate} />)}
            {/* A failed next page keeps the rows already on screen and says so under them. */}
            {listError && (
              <div data-load-more-failed role="alert" style={{ marginTop: 4, padding: '12px 14px', borderRadius: 'var(--radius-md)', background: TONE.bad.bg, color: TONE.bad.color, border: `1px solid ${TONE.bad.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                Couldn&apos;t load the next page: {listError}
                <div style={{ marginTop: 8 }}><Btn isMobile={isMobile} onClick={() => loadRows(rows.length)}>Try again</Btn></div>
              </div>
            )}
            <div style={{ textAlign: 'center', marginTop: 8, fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>
              Showing {rows.length.toLocaleString()}{kindCount[kind] != null ? ` of ${kindCount[kind].toLocaleString()}` : ''}
            </div>
            {more && !listError && (
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Btn isMobile={isMobile} onClick={() => loadRows(rows.length)}>Show more</Btn>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
